import { CheckCircleIcon, CheckIcon, ClockIcon, CopyIcon, MinusCircleIcon, PlusIcon, ProhibitIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type FormEvent, useId, useState } from "react";
import { api, q } from "./api.ts";
import { list, number } from "./format.ts";
import { PERSONAL_TOKEN_LABELS as L } from "./labels.ts";
import { Button, Callout, CopyButton, Empty, ErrorText, Field, Modal, Section } from "./ui.tsx";
import { AgentLogo, type AgentEngine } from "./ui/props.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { Chip } from "./ui/status.tsx";

/** Only known OAuth client names are shortened; person-written token names stay intact. */
function identity(token: C.PersonalToken): { name: string; original: string; engine?: AgentEngine } {
  const original = token.source === "oauth" ? token.oauth_client?.name ?? token.name : token.name;
  if (token.source === "oauth") {
    if (/^Claude Code(?: \(plugin:zooi:zooi\))?$/i.test(original)) return { name: "Claude Code", original, engine: "claude_code" };
    if (/^Codex$/i.test(original)) return { name: "Codex", original, engine: "codex" };
  }
  return { name: original, original };
}

function TokenRow({ token, products, now, onRevoke, history = false }: { token: C.PersonalToken; products: C.Product[]; now: number; onRevoke: (token: C.PersonalToken) => void; history?: boolean }) {
  const expired = Date.parse(token.expires_at) <= now;
  const active = !token.revoked_at && !expired;
  const soon = active && Date.parse(token.expires_at) - now <= 7 * 86_400_000;
  const who = identity(token);
  const idle = active ? token.idle : null;
  const Icon = token.revoked_at ? ProhibitIcon : idle ? MinusCircleIcon : expired || soon ? ClockIcon : CheckCircleIcon;
  return <div className="z-row z-token-row">
    <div className="z-row-main">
      <div className="z-token-heading">
        <span className="z-token-name" title={who.original}>{who.engine && <AgentLogo engine={who.engine} />}<span className="z-row-title">{who.name}</span></span>
        <Chip tone={soon && !idle ? "amber" : "gray"} variant={soon && !idle ? "soft" : "outline"} icon={<Icon size={12} aria-hidden />}>{token.revoked_at ? L.revoked : expired ? L.expired : idle ? L.idle : soon ? L.expiring : L.active}</Chip>
      </div>
      <p className="z-row-body">{[history ? token.source === "oauth" ? L.viaOAuth : L.manualToken : null, token.scopes[0] === "agent" ? L.agentAccess : L.readOnly, L.accessibleProducts(list(token.product_ids.map((id) => products.find((product) => product.id === id)?.name ?? L.unavailableProduct)))].filter(Boolean).join(" · ")}</p>
      {idle && <p className="z-row-body">{L.idleNote(who.name, idle.revokes_at)}</p>}
      <div className="z-row-meta">
        <time dateTime={token.created_at}>{token.source === "oauth" ? L.authorized(token.created_at) : L.created(token.created_at)}</time>
        {token.revoked_at && <time dateTime={token.revoked_at}>{L.revokedAt(token.revoked_at)}</time>}
        <time dateTime={token.expires_at}>{expired ? L.expiredAt(token.expires_at) : L.expires(token.expires_at)}</time>
        {token.last_used_at ? <time dateTime={token.last_used_at}>{L.lastUsed(token.last_used_at)}</time> : <span>{L.lastUsed(null)}</span>}
      </div>
    </div>
    {active && <div className="z-row-side"><Button size="sm" tone="danger" aria-label={L.revokeTitle(who.name)} onClick={() => onRevoke(token)}>{L.revoke}</Button></div>}
  </div>;
}

/** A newly issued secret stays in this component, never query or mutation caches. */
function IssuedToken({ issued, onDismiss }: { issued: C.IssuedPersonalToken; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const copy = async () => {
    setError(null);
    try { await navigator.clipboard.writeText(issued.secret); setCopied(true); }
    catch { setError(new Error(L.copyFailed)); }
  };
  return <Callout><div className="z-flow">
    <p className="z-row-title">{L.secretTitle}</p>
    <p>{L.secretNote}</p>
    <Field label={L.secret}><input className="z-input z-mono" type="password" aria-label={L.secret} value={issued.secret} readOnly autoComplete="off" spellCheck={false} /></Field>
    <p className="z-small z-muted">{L.expires(issued.token.expires_at)}</p>
    <div className="z-inline"><Button size="sm" tone="ghost" icon={copied ? <CheckIcon /> : <CopyIcon />} onClick={copy}>{copied ? L.copied : L.copy}</Button><Button size="sm" onClick={onDismiss}>{L.done}</Button></div>
    <ErrorText error={error} />
  </div></Callout>;
}

/** Product-scoped access and its history (docs/design-system.md §0.4g); governance always requires a person's session. */
export function PersonalTokens({ workspaceId, userId, onCreatingChange }: { workspaceId: string; userId: string; onCreatingChange?: (creating: boolean) => void }) {
  const navigate = useNavigate();
  const createOpen = useRouterState({ select: (state) => state.location.hash === "create-token" });
  const setCreateOpen = (open: boolean) => { if (!creating) void navigate({ to: "/settings/tokens", search: true, hash: open ? "create-token" : "", replace: true }); };
  const formId = useId();
  const client = useQueryClient();
  const query = q.personalTokens(workspaceId, userId);
  const tokens = useQuery(query);
  const products = useQuery(q.products);
  const [name, setName] = useState("");
  const [productIds, setProductIds] = useState<string[]>([]);
  const [days, setDays] = useState(30);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [issued, setIssued] = useState<C.IssuedPersonalToken | null>(null);
  const [revoking, setRevoking] = useState<C.PersonalToken | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: query.queryKey });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/personal-tokens/${encodeURIComponent(id)}`),
    onSuccess: async () => { setRevoking(null); await refresh(); },
  });
  const availableProducts = products.data ?? [];
  const selectedIds = productIds.filter((id) => availableProducts.some((product) => product.id === id));
  const valid = name.trim().length > 0 && selectedIds.length > 0 && Number.isInteger(days) && days >= 1 && days <= 90;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || creating || issued) return;
    setCreating(true);
    onCreatingChange?.(true);
    setCreateError(null);
    try {
      const result = await api.post<C.IssuedPersonalToken>("/api/personal-tokens", { name: name.trim(), product_ids: selectedIds, scopes: ["read"], expires_in_days: days });
      setIssued(result);
      setName("");
      setProductIds([]);
      setDays(30);
      void navigate({ to: "/settings/tokens", search: true, hash: "", replace: true });
      await refresh();
    } catch (error) { setCreateError(error); }
    finally { setCreating(false); onCreatingChange?.(false); }
  };
  const endpoint = `${window.location.origin}/mcp`;
  const now = Date.now();
  const current = (tokens.data ?? []).filter((token) => !token.revoked_at && Date.parse(token.expires_at) > now);
  const history = (tokens.data ?? []).filter((token) => token.revoked_at || Date.parse(token.expires_at) <= now);
  const oauth = current.filter((token) => token.source === "oauth");
  const manual = current.filter((token) => token.source === "manual");
  const rows = (items: C.PersonalToken[], past = false) => <div className="z-boxed">{items.map((token) => <TokenRow key={token.id} token={token} products={availableProducts} now={now} history={past} onRevoke={(item) => { revoke.reset(); setRevoking(item); }} />)}</div>;
  return <>
    <p className="z-section-note">{L.note}</p>
    {issued && <IssuedToken key={issued.token.id} issued={issued} onDismiss={() => setIssued(null)} />}
    <Section title={L.oauthConnections} count={tokens.isSuccess ? oauth.length : undefined}>
      {tokens.isLoading || products.isLoading ? <Skeleton shape="list" rows={2} /> : tokens.isSuccess && (oauth.length ? rows(oauth) : <Empty>{L.noOAuth}</Empty>)}
    </Section>
    <Section title={L.manualTokens} count={tokens.isSuccess ? manual.length : undefined} actions={<Button size="sm" icon={<PlusIcon />} disabled={issued !== null} onClick={() => setCreateOpen(true)}>{L.create}</Button>}>
      {tokens.isLoading || products.isLoading ? <Skeleton shape="list" rows={1} /> : tokens.isSuccess && (manual.length ? rows(manual) : <Empty>{L.noManual}</Empty>)}
    </Section>
    <ErrorText error={tokens.error} />
    <ErrorText error={products.error} />
    {history.length > 0 && <details className="z-disclosure">
      <summary>{L.history}<span className="z-count">{number(history.length)}</span></summary>
      {products.isLoading ? <Skeleton shape="list" rows={1} /> : rows(history, true)}
    </details>}
    <Modal open={createOpen} onOpenChange={setCreateOpen} title={L.create} footer={<>
      <Button disabled={creating} onClick={() => setCreateOpen(false)}>{L.cancel}</Button>
      <Button tone="primary" type="submit" form={formId} busy={creating} disabled={!valid || issued !== null}>{L.create}</Button>
    </>}>
      <form id={formId} className="z-form z-flow" onSubmit={submit}>
        <Field label={L.name}><input className="z-input" aria-label={L.name} placeholder={L.namePlaceholder} value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required disabled={creating} /></Field>
        <div className="z-flow" role="group" aria-labelledby={`${formId}-products`}>
          <h3 id={`${formId}-products`}>{L.products}</h3>
          <p className="z-small z-muted">{L.productsHelp}</p>
          {products.isLoading ? <Skeleton shape="text" rows={2} /> : availableProducts.length ? <ul className="z-check">
            {availableProducts.map((product) => <li key={product.id}><input id={`token-product-${product.id}`} type="checkbox" checked={productIds.includes(product.id)} disabled={creating} onChange={(event) => setProductIds((ids) => event.target.checked ? [...ids, product.id] : ids.filter((id) => id !== product.id))} /><label htmlFor={`token-product-${product.id}`}>{product.name}</label></li>)}
          </ul> : products.isSuccess && <Empty>{L.noProducts}</Empty>}
          <ErrorText error={products.error} />
        </div>
        <Field label={L.expiry} help={L.expiryHelp}><input className="z-input" type="number" aria-label={L.expiry} min={1} max={90} step={1} value={days} onChange={(event) => setDays(Number(event.target.value))} required disabled={creating} /></Field>
        <p className="z-small z-muted">{L.accessNote}</p>
        <ErrorText error={createError} />
      </form>
    </Modal>
    <details className="z-disclosure">
      <summary>{L.connect}</summary>
      <p className="z-small z-muted">{L.connectionHelp}</p>
      <dl className="z-props"><dt>{L.endpoint}</dt><dd className="z-mono">{endpoint}<CopyButton text={endpoint} label={L.copyEndpoint} /></dd></dl>
    </details>
    <Modal open={revoking !== null} onOpenChange={(open) => { if (!open && !revoke.isPending) setRevoking(null); }} title={L.revokeTitle(revoking ? identity(revoking).name : "")} footer={<>
      <Button disabled={revoke.isPending} onClick={() => setRevoking(null)}>{L.cancel}</Button>
      <Button tone="danger" busy={revoke.isPending} onClick={() => { if (revoking) revoke.mutate(revoking.id); }}>{L.revokeConfirm}</Button>
    </>}><p>{L.revokeNote}</p><ErrorText error={revoke.error} /></Modal>
  </>;
}
